import React, { useState } from 'react';
import { Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import OopsWindow from '../Oops';
import LoadingIndicatorLogo from '../../components/LoadingIndicatorLogo';
import ACSnackbar from '../../components/ACSnackbar';
import PasswordForm from '../../components/PasswordSet/PasswordForm';
import UnhandledError from '../../components/PasswordSet/UnhandledError';
import ExpiredLink from '../../components/PasswordSet/ExpiredError';
import LoginError from '../../components/PasswordSet/LoginError';
import {
  forgotPassword,
  forgotPasswordSubmit,
  logIn,
} from '../../services/Authentication';
import { useAccount } from '../../contexts/Account';
import {
  ForgotPasswordResponses,
  ForgotPasswordErrorResponses,
  LoginResponseTypes,
  hasUrlParams,
} from '../../services/Authentication/constants';
import PublicRegistration from '../../layouts/PublicRegistration';
import useLoginRedirect from '../../hooks/useLoginRedirect';

const useStyles = () => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '12px',
    width: 'auto',
    backgroundColor: 'white',
  },
  indicatorTitle: {
    color: '#000000',
    marginBottom: '20px',
    fontSize: '28px',
    lineHeight: '24px',
    fontWeight: 700,
    letterSpacing: '-0.4300000071525574px',
    textAlign: 'center',
  },
  indicatorText: {
    color: '#646D7A',
    marginBottom: '40px',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '-0.4300000071525574px',
    textAlign: 'center',
  },
  loadingContainer: {
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
  },
});

// TODO this array can be removed some time it causes additional function that make no sense
// this array was created by ivan when he create the component due to several refactors
// it may not be needed
const SCENE_TITLES_KEYS = [
  'CREATE-PASSWORD-INDICATOR-TEXT',
  'LOGGING-ACCOUNT-INDICATOR-TEXT',
  'ERROR-INDICATOR-TEXT',
];

const SCENE_STATE_TYPES = {
  CREATE_PASSWORD: 'CREATE_PASSWORD',
  EXPIRED_LINK: 'EXPIRED_LINK',
  UNHANDLED_ERROR: 'UNHANDLED_ERROR',
  LOGIN_ERROR: 'LOGIN_ERROR',
  SIGN_IN_LOADING: 'SIGN_IN_LOADING',
};

function PasswordSet({ location }) {
  const { authenticate } = useAccount();
  const [sceneId, setSceneId] = useState(SCENE_STATE_TYPES.CREATE_PASSWORD);
  const [password, setPassword] = useState('');
  const [logInResponse, setLogInResponse] = useState(null);
  const [isErrorSnackbarVisible, setIsErrorSnackbarVisible] = useState(false);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [indicatorTranslationKey, setIndicatorTranslationKey] = useState(
    SCENE_TITLES_KEYS[0]
  );
  const [isPasswordFormLoading, setIsPasswordFormLoading] = useState(false);
  const [isAbleToRetry, setIsAbleToRetry] = useState(false);
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const queryParams = new URLSearchParams(location.search);
  const [email, setEmail] = useState();
  const [code, setCode] = useState();

  useLoginRedirect({ logInResponse });

  function convertSceneStateTypeToSceneTitleKeys(sceneIdParam) {
    switch (sceneIdParam) {
      case SCENE_STATE_TYPES.CREATE_PASSWORD:
        return SCENE_TITLES_KEYS[0];
      case SCENE_STATE_TYPES.EXPIRED_LINK:
        return SCENE_TITLES_KEYS[2];
      case SCENE_STATE_TYPES.UNHANDLED_ERROR:
        return SCENE_TITLES_KEYS[2];
      case SCENE_STATE_TYPES.LOGIN_ERROR:
        return SCENE_TITLES_KEYS[2];
      case SCENE_STATE_TYPES.SIGN_IN_LOADING:
        return SCENE_TITLES_KEYS[1];
      default:
        return SCENE_TITLES_KEYS[0];
    }
  }

  async function signInProcess(data = password) {
    setIndicatorTranslationKey(
      convertSceneStateTypeToSceneTitleKeys(SCENE_STATE_TYPES.SIGN_IN_LOADING)
    );
    const response = await logIn(email, data, authenticate, true);
    if (response.response === LoginResponseTypes.LOGIN_SUCCESS) {
      setLogInResponse(response);
    }
  }

  function handleSignIn(data = password) {
    signInProcess(data);
  }

  async function handleFailedCode() {
    await forgotPassword(email, { workflow: 'signup' });
  }

  function handleForgotPasswordSubmit(result, data = password) {
    if (result === ForgotPasswordResponses.SUCCESS) {
      signInProcess(data);
    } else if (result === ForgotPasswordErrorResponses.EXPIRED_CODE) {
      setSceneId(SCENE_STATE_TYPES.EXPIRED_LINK);
      setIndicatorTranslationKey(
        convertSceneStateTypeToSceneTitleKeys(SCENE_STATE_TYPES.EXPIRED_LINK)
      );
      handleFailedCode();
    } else if (result === ForgotPasswordResponses.CODE_MISMATCH_EXCEPTION) {
      setSceneId(SCENE_STATE_TYPES.EXPIRED_LINK);
      setIndicatorTranslationKey(
        convertSceneStateTypeToSceneTitleKeys(SCENE_STATE_TYPES.EXPIRED_LINK)
      );
      handleFailedCode();
    } else {
      if (
        result === ForgotPasswordErrorResponses.LIMIT_EXCEEDED ||
        result === ForgotPasswordErrorResponses.TOO_MANY_REQUESTS
      ) {
        setIsAbleToRetry(true);
      }
      setSceneId(SCENE_STATE_TYPES.UNHANDLED_ERROR);
      setIndicatorTranslationKey(
        convertSceneStateTypeToSceneTitleKeys(SCENE_STATE_TYPES.UNHANDLED_ERROR)
      );
    }
  }

  async function handleContinueButton(data) {
    setSceneId(SCENE_STATE_TYPES.SIGN_IN_LOADING);
    setPassword(data);
    setIsPasswordFormLoading(true);
    const result = await forgotPasswordSubmit(email, code, data);
    handleForgotPasswordSubmit(result, data);
    setIsPasswordFormLoading(false);
    return result;
  }

  async function handleUnhandledErrorRetryButton() {
    const result = handleContinueButton();
    if (result !== ForgotPasswordResponses.SUCCESS) {
      setIsErrorSnackbarVisible(true);
    }
  }

  async function handleUnhandledErrorResendButton() {
    const result = await forgotPassword(email, {
      workflow: 'signup',
    });
    if (result) {
      setIsSnackbarVisible(true);
    } else {
      setIsErrorSnackbarVisible(true);
    }
  }

  function renderHelmetTranslationKey() {
    switch (sceneId) {
      case SCENE_STATE_TYPES.CREATE_PASSWORD:
        return 'PASSWORD-SET-HELMET-TITLE';
      case SCENE_STATE_TYPES.SIGN_IN_LOADING:
        return 'PASSWORD-SET-HELMET-TITLE';
      case SCENE_STATE_TYPES.LOGIN_ERROR:
        return 'ACCOUNT-LOGIN-ERROR-HELMET-TITLE';
      default:
        return 'PASSWORD-SET-ERROR-HELMET-TITLE';
    }
  }

  const [displayedComponent] = useState(() => {
    const getEmail = queryParams.get('email');
    const getCode = queryParams.get('code');

    if (getEmail !== null && getCode !== null) {
      setEmail(queryParams.get('email').replace(/\s/g, '+'));
      setCode(getCode);
      return hasUrlParams.PARAMS;
    }

    return hasUrlParams.NO_PARAMS;
  });

  function renderScene() {
    switch (sceneId) {
      case SCENE_STATE_TYPES.CREATE_PASSWORD:
        return (
          <PasswordForm
            buttonText={t('CONTINUE-BUTTON-TEXT')}
            isLoading={isPasswordFormLoading ? 1 : 0}
            isSM={isSM}
            onContinue={(pass) => handleContinueButton(pass)}
            passwordLabel={t('PASSWORD-LABEL')}
          />
        );
      case SCENE_STATE_TYPES.EXPIRED_LINK:
        return (
          <ExpiredLink
            isSM={isSM}
            onResend={handleUnhandledErrorResendButton}
            email={email}
          />
        );
      case SCENE_STATE_TYPES.UNHANDLED_ERROR:
        return (
          <UnhandledError
            isAbleToRetry={isAbleToRetry}
            onResend={handleUnhandledErrorResendButton}
            onRetry={handleUnhandledErrorRetryButton}
            isSM={isSM}
          />
        );
      case SCENE_STATE_TYPES.SIGN_IN_LOADING:
        return (
          <div style={classes.loadingContainer}>
            <LoadingIndicatorLogo iconFontSize={133} iconRight={160} />
          </div>
        );
      case SCENE_STATE_TYPES.LOGIN_ERROR:
        return <LoginError onRetry={handleSignIn} />;

      default:
        return null;
    }
  }

  const ReturnWindow = () => {
    if (displayedComponent === hasUrlParams.PARAMS) {
      return (
        <div style={classes.container}>
          <Helmet>
            <title>{t(renderHelmetTranslationKey())}</title>
          </Helmet>

          <ACSnackbar
            id="password-set-retry-snackbar"
            text={t('CONFIRMATION-REQUEST-RECEIVED-LABEL')}
            onClose={() => setIsSnackbarVisible(false)}
            open={isSnackbarVisible}
          />

          <ACSnackbar
            id="password-set-error-snackbar"
            text={t('ERROR-SNACKBAR-TEXT')}
            onClose={() => setIsErrorSnackbarVisible(false)}
            open={isErrorSnackbarVisible}
            severity="error"
          />
          <PublicRegistration>
            <Typography sx={classes.indicatorTitle}>
              Welcome to AdvisoryCloud
            </Typography>
            <Typography sx={classes.indicatorText}>
              {t(indicatorTranslationKey)}
            </Typography>
            {renderScene()}
          </PublicRegistration>
        </div>
      );
    }

    if (displayedComponent === hasUrlParams.NO_PARAMS) {
      // TODO in case is view after product lunch. Remove this and below comment
      // history.push('/');
      return (
        <>
          <Helmet>
            <title>{t('PASSWORD_RESET_HEADER_TITLE')}</title>
          </Helmet>
          <OopsWindow />
        </>
      );
    }

    return <></>;
  };

  return (
    <>
      <ReturnWindow />
    </>
  );
}

export default PasswordSet;
